import { useState, type ReactNode } from 'react';
import type { ProjectGraphEdge, ProjectGraphResponse } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';
import { Button } from '../../catalyst/button';
import { formatShortId } from '../../components/ID';
import { Link } from '../../components/Link';
import { DocumentFacts } from './DocumentFacts';
import { EntityHeading, Identifier } from './EntityHeading';
import { findingsForNode, ruleFindingsForNode } from './findings';
import { incomingMentions, indexOf } from './graphIndex';
import { nodeLabel } from './graphModel';
import { MentionRow } from './MentionRow';
import { NodeFindings } from './NodeFindings';
import { NodeRuleFindings } from './NodeRuleFindings';
import { ReadingVariants } from './ReadingVariants';
import { SelectionHeader } from './SelectionHeader';
import { VirtualList } from './VirtualList';

type Props = {
  graph: ProjectGraphResponse;
  selectedId: string | null;
  focused: boolean;
  /** Buttons shown beside Focus / Clear focus. */
  actions?: ReactNode;
  /** The filter label (`Parts`, `LLP Status`) of each neighbour a filter hides. */
  hiddenFilters: ReadonlyMap<string, string>;
  onFocus: (id: string | null) => void;
  onSelect: (id: string) => void;
  /** Clears the filter that hides a neighbour, then selects it as `onSelect` does. */
  onShowAndReveal: (id: string) => void;
};

/** A mention row's height with one line of label, spacing included; taller rows are measured. */
const MENTION_ROW_HEIGHT = 32;
const edgeKey = (edge: ProjectGraphEdge) => edge.id;

const statusColor = (status: string | null) => (status === 'done' ? 'green' : status === 'error' ? 'red' : 'zinc');

export function GraphSidebar({
  graph,
  selectedId,
  focused,
  actions,
  hiddenFilters,
  onFocus,
  onSelect,
  onShowAndReveal,
}: Props) {
  // The edges whose evidence is open. The parent keys this component by the selection, so a new one starts
  // with every row closed.
  const [openEdges, setOpenEdges] = useState<ReadonlySet<string>>(() => new Set());
  const index = indexOf(graph);
  const { nodesById, outgoing: outgoingById } = index;
  const node = selectedId === null ? undefined : nodesById.get(selectedId);
  if (!node) {
    return (
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        {selectedId === null ? 'Select a node to see its details.' : 'This node is not in the graph.'}
      </p>
    );
  }
  const labelOf = (id: string) => {
    const other = nodesById.get(id);
    return other ? nodeLabel(other) : id;
  };
  const mentions = incomingMentions(index, node.id);
  const findings = findingsForNode(graph.findings, node.id);
  const ruleFindings = ruleFindingsForNode(graph.ruleFindings, node.id);
  const outgoing = outgoingById.get(node.id) ?? [];
  const row = (edge: ProjectGraphEdge, otherId: string) => (
    <MentionRow
      edge={edge}
      label={labelOf(otherId)}
      hiddenFilter={hiddenFilters.get(otherId) ?? null}
      onSelect={() => onSelect(otherId)}
      onShowAndReveal={() => onShowAndReveal(otherId)}
      open={openEdges.has(edgeKey(edge))}
      onToggle={() =>
        setOpenEdges((prev) => {
          const next = new Set(prev);
          if (!next.delete(edgeKey(edge))) next.add(edgeKey(edge));
          return next;
        })
      }
    />
  );

  return (
    <div className="flex flex-col gap-3 text-sm text-zinc-900 dark:text-zinc-100">
      <SelectionHeader kind={node.kind}>
        {actions}
        <Button plain onClick={() => onFocus(focused ? null : node.id)}>
          {focused ? 'Clear focus' : 'Focus'}
        </Button>
      </SelectionHeader>
      {node.kind === 'document' ? (
        <>
          <div className="font-medium break-words">{node.title}</div>
          <div className="text-zinc-500 dark:text-zinc-400">
            {node.category ?? 'Uncategorised'} · {node.pageCount} {node.pageCount === 1 ? 'page' : 'pages'}
          </div>
          <div className="text-zinc-500 dark:text-zinc-400">
            <Identifier
              label="Document"
              value={formatShortId(node.documentId)}
              copyText={node.documentId}
              tooltip="Copy document ID"
            />
          </div>
          <div>
            <Badge color={statusColor(node.extractionStatus)}>{node.extractionStatus ?? 'no extraction'}</Badge>
          </div>
          <Link to={`/reconstructed-documents/${encodeURIComponent(node.reconstructedDocumentId)}`}>
            Open reconstructed document
          </Link>
          {node.facts && <DocumentFacts facts={node.facts} />}
          <NodeFindings findings={findings} />
          <NodeRuleFindings ruleFindings={ruleFindings} nodesById={nodesById} />
          <div className="mt-2 font-medium">
            Mentions {outgoing.length} {outgoing.length === 1 ? 'entity' : 'entities'}
          </div>
          <VirtualList
            items={outgoing}
            getKey={edgeKey}
            rowHeight={MENTION_ROW_HEIGHT}
            spacing={2}
            renderRow={(e) => row(e, e.to)}
          />
        </>
      ) : (
        <>
          <EntityHeading node={node} />
          <ReadingVariants
            readingVariants={node.kind === 'part' || node.kind === 'engine' ? node.readingVariants : undefined}
          />
          {node.kind === 'engine' && node.coverage && (
            <div className="text-zinc-500 dark:text-zinc-400">
              Coverage: evidenced {node.coverage.evidenced} of {node.coverage.expected} expected, traced{' '}
              {node.coverage.traced}
              {node.coverage.llp &&
                `; LLP evidenced ${node.coverage.llp.evidenced} of ${node.coverage.llp.expected}, traced ${node.coverage.llp.traced}`}
            </div>
          )}
          <NodeFindings findings={findings} />
          <NodeRuleFindings ruleFindings={ruleFindings} nodesById={nodesById} />
          <div className="mt-2 font-medium">
            Mentioned by {mentions.length} {mentions.length === 1 ? 'document' : 'documents'}
          </div>
          <VirtualList
            items={mentions}
            getKey={edgeKey}
            rowHeight={MENTION_ROW_HEIGHT}
            spacing={2}
            renderRow={(e) => row(e, e.from)}
          />
        </>
      )}
    </div>
  );
}
